import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { BRANCHES } from '@/mocks/mockData';

type LessonStatus = 'yapıldı' | 'ertelendi' | 'iptal' | 'katılmadı';

interface DersRow {
  id: string;
  date: string;
  time: string;
  studentId: string;
  studentName: string;
  branch: string;
  duration: number;
  topic: string;
  homeworkGiven: boolean;
  quizDone: boolean;
  performanceNote: string;
  status: string;
  paymentStatus: string;
}

interface StudentOption { id: string; name: string; branches: string[]; }

const statusColors: Record<string, string> = {
  yapıldı: 'bg-green-100 text-green-700',
  ertelendi: 'bg-yellow-100 text-yellow-700',
  iptal: 'bg-red-100 text-red-700',
  katılmadı: 'bg-orange-100 text-orange-700',
};

const statusLabels: Record<string, string> = {
  yapıldı: 'Yapıldı', ertelendi: 'Ertelendi', iptal: 'İptal', katılmadı: 'Öğrenci Katılmadı',
};

const emptyForm = {
  studentId: '', branch: '', date: '', time: '', duration: 60,
  topic: '', homeworkGiven: false, homeworkDesc: '', quizDone: false, quizResult: '',
  performanceNote: '', repeatTopic: '', parentInformed: false, status: 'yapıldı' as LessonStatus, note: '',
};

export default function AktifDerslerimPage() {
  const { ogretmenRecord } = useAuth();
  const teacherId = ogretmenRecord?.id ?? null;

  const [data, setData] = useState<DersRow[]>([]);
  const [students, setStudents] = useState<StudentOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [toast, setToast] = useState('');

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const fetchData = useCallback(async () => {
    if (!teacherId) return;
    setLoading(true);

    const [dersRes, ogrenciRes] = await Promise.all([
      supabase
        .from('egtpanel_aktif_dersler')
        .select('*')
        .eq('ogretmen_id', teacherId)
        .order('ders_tarihi', { ascending: false }),
      supabase
        .from('egtpanel_ogrenciler')
        .select('id, ogrenci_adi, branslar')
        .eq('atanan_ogretmen_id', teacherId)
        .eq('aktif', true),
    ]);

    const ogrenciMap: Record<string, string> = {};
    const stuList: StudentOption[] = [];
    (ogrenciRes.data ?? []).forEach((s: { id: string; ogrenci_adi: string; branslar: string[] }) => {
      ogrenciMap[s.id] = s.ogrenci_adi;
      stuList.push({ id: s.id, name: s.ogrenci_adi, branches: Array.isArray(s.branslar) ? s.branslar : [] });
    });
    setStudents(stuList);

    if (dersRes.data) {
      setData(dersRes.data.map((d) => ({
        id: d.id,
        date: d.ders_tarihi,
        time: d.ders_saati ? d.ders_saati.slice(0, 5) : '',
        studentId: d.ogrenci_id,
        studentName: ogrenciMap[d.ogrenci_id] ?? 'Öğrenci',
        branch: d.brans ?? '',
        duration: d.ders_suresi ?? 40,
        topic: d.islenen_konu ?? '',
        homeworkGiven: d.odev_verildi ?? false,
        quizDone: d.mini_sinav ?? false,
        performanceNote: d.ogrenci_performans_notu ?? '',
        status: d.ders_durumu ?? 'yapıldı',
        paymentStatus: d.odeme_durumu ?? 'bekliyor',
      })));
    }
    setLoading(false);
  }, [teacherId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const selectedStudent = students.find((s) => s.id === form.studentId);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!teacherId) return;
    setSaving(true);

    const { error } = await supabase.from('egtpanel_aktif_dersler').insert({
      ogrenci_id: form.studentId,
      ogretmen_id: teacherId,
      brans: form.branch || null,
      ders_tarihi: form.date,
      ders_saati: form.time || null,
      ders_suresi: form.duration,
      islenen_konu: form.topic || null,
      odev_verildi: form.homeworkGiven,
      odev_aciklamasi: form.homeworkDesc || null,
      mini_sinav: form.quizDone,
      sinav_sonucu: form.quizResult || null,
      ogrenci_performans_notu: form.performanceNote || null,
      tekrar_konusu: form.repeatTopic || null,
      veli_bilgilendirildi: form.parentInformed,
      ders_durumu: form.status,
      ek_aciklama: form.note || null,
    });

    setSaving(false);
    if (error) {
      showToast('Hata: Ders kaydedilemedi.');
    } else {
      showToast('Ders başarıyla kaydedildi!');
      setShowModal(false);
      setForm(emptyForm);
      fetchData();
    }
  };

  const now = new Date();
  const monthStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

  return (
    <div className="p-6 space-y-6">
      {toast && (
        <div className="fixed top-4 right-4 z-50 bg-purple-600 text-white px-5 py-3 rounded-xl text-sm font-medium">
          {toast}
        </div>
      )}

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Aktif Derslerim</h1>
          <p className="text-slate-500 text-sm mt-1">Yaptığınız dersleri kaydedin</p>
        </div>
        <button onClick={() => setShowModal(true)} className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors whitespace-nowrap cursor-pointer">
          <i className="ri-add-line" /> Ders Gir
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Bu Ay Ders', val: data.filter((d) => d.date.startsWith(monthStr)).length, color: 'text-purple-700', bg: 'bg-purple-50' },
          { label: 'Yapıldı', val: data.filter((d) => d.status === 'yapıldı').length, color: 'text-green-700', bg: 'bg-green-50' },
          { label: 'Ödeme Bekliyor', val: data.filter((d) => d.paymentStatus === 'bekliyor').length, color: 'text-amber-700', bg: 'bg-amber-50' },
          { label: 'Ödev Verilen', val: data.filter((d) => d.homeworkGiven).length, color: 'text-indigo-700', bg: 'bg-indigo-50' },
        ].map((s) => (
          <div key={s.label} className={`${s.bg} rounded-xl p-4`}>
            <p className="text-xs text-slate-500 font-medium">{s.label}</p>
            <p className={`text-2xl font-bold mt-1 ${s.color}`}>{s.val}</p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <i className="ri-loader-4-line text-3xl text-slate-300 animate-spin" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  {['Tarih / Saat', 'Öğrenci', 'Branş', 'Konu', 'Süre', 'Ödev', 'Durum', 'Ödeme'].map((h) => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {data.length === 0 ? (
                  <tr><td colSpan={8} className="text-center py-16">
                    <i className="ri-book-open-line text-4xl text-slate-200 block mb-2" />
                    <p className="text-slate-400 text-sm">{teacherId ? 'Henüz ders girilmemiş' : 'Öğretmen kaydı bulunamadı'}</p>
                  </td></tr>
                ) : data.map((l) => (
                  <tr key={l.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="font-medium text-slate-800">{l.date}</div>
                      <div className="text-xs text-slate-400">{l.time}</div>
                    </td>
                    <td className="px-4 py-3 font-medium text-slate-800 whitespace-nowrap">{l.studentName}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full text-xs font-medium">{l.branch || '-'}</span>
                    </td>
                    <td className="px-4 py-3 text-slate-600 max-w-[150px] truncate">{l.topic || '-'}</td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{l.duration} dk</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {l.homeworkGiven ? <span className="bg-green-50 text-green-700 px-2 py-0.5 rounded-full text-xs">Verildi</span> : <span className="bg-slate-50 text-slate-400 px-2 py-0.5 rounded-full text-xs">Yok</span>}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusColors[l.status] ?? 'bg-gray-100 text-gray-600'}`}>{statusLabels[l.status] ?? l.status}</span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${l.paymentStatus === 'ödendi' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {l.paymentStatus === 'ödendi' ? 'Ödendi' : 'Bekliyor'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-slate-100">
              <h2 className="text-lg font-bold text-slate-800">Ders Girişi</h2>
              <button onClick={() => setShowModal(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400 cursor-pointer">
                <i className="ri-close-line" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-slate-600 mb-1">Öğrenci Seç *</label>
                  <select required value={form.studentId} onChange={(e) => setForm((p) => ({ ...p, studentId: e.target.value, branch: '' }))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20">
                    <option value="">— Seçiniz —</option>
                    {students.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Branş *</label>
                  <select required value={form.branch} onChange={(e) => setForm((p) => ({ ...p, branch: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20">
                    <option value="">— Seçiniz —</option>
                    {(selectedStudent?.branches.length ? selectedStudent.branches : BRANCHES).map((b) => <option key={b}>{b}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Süre (dk)</label>
                  <select value={form.duration} onChange={(e) => setForm((p) => ({ ...p, duration: Number(e.target.value) }))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20">
                    {[20, 30, 40, 45, 60, 90].map((d) => <option key={d} value={d}>{d} dk</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Tarih *</label>
                  <input required type="date" value={form.date} onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Saat</label>
                  <input type="time" value={form.time} onChange={(e) => setForm((p) => ({ ...p, time: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">İşlenen Konu *</label>
                <input required value={form.topic} onChange={(e) => setForm((p) => ({ ...p, topic: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20" />
              </div>
              <div className="flex flex-wrap gap-4">
                {[
                  { label: 'Ödev verildi', key: 'homeworkGiven', val: form.homeworkGiven },
                  { label: 'Mini sınav yapıldı', key: 'quizDone', val: form.quizDone },
                  { label: 'Veliye bilgi verildi', key: 'parentInformed', val: form.parentInformed },
                ].map((f) => (
                  <label key={f.key} className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={f.val} onChange={(e) => setForm((p) => ({ ...p, [f.key]: e.target.checked }))} className="rounded" />
                    <span className="text-sm text-slate-700">{f.label}</span>
                  </label>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Performans Notu</label>
                  <input value={form.performanceNote} onChange={(e) => setForm((p) => ({ ...p, performanceNote: e.target.value }))} placeholder="İyi / Orta / Zayıf..." className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Tekrar Gerekli Konu</label>
                  <input value={form.repeatTopic} onChange={(e) => setForm((p) => ({ ...p, repeatTopic: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Ders Durumu</label>
                <select value={form.status} onChange={(e) => setForm((p) => ({ ...p, status: e.target.value as LessonStatus }))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20">
                  <option value="yapıldı">Yapıldı</option>
                  <option value="ertelendi">Ertelendi</option>
                  <option value="iptal">İptal</option>
                  <option value="katılmadı">Öğrenci Katılmadı</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Ek Açıklama</label>
                <textarea value={form.note} onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))} rows={2} maxLength={500} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20 resize-none" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 py-2.5 rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 cursor-pointer">İptal</button>
                <button type="submit" disabled={saving} className="flex-1 py-2.5 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 disabled:opacity-60 cursor-pointer">
                  {saving ? 'Kaydediliyor...' : 'Kaydet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}